import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { APP_COLORS as C } from "../../theme/colors";

const NUMERIC = /^(double|real|integer|bigint|smallint|numeric)/;

/**
 * Floating attribute card for the selected sketch feature. Remount it (key)
 * per feature so the local values start from the feature's current properties.
 */
export default function AttributeForm({
  title,
  geometryLabel,
  columns,
  properties,
  editable,
  onApply,
  onDelete,
  onClose,
}) {
  const [values, setValues] = useState(() => {
    const v = {};
    columns.forEach(({ name }) => {
      v[name] = properties[name] ?? "";
    });
    return v;
  });

  return (
    <Paper
      elevation={6}
      sx={{
        position: "absolute",
        top: 12,
        right: 12,
        width: 300,
        maxHeight: "calc(100% - 24px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
        zIndex: 6,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          color: "#fff",
          bgcolor: C.primary,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }} noWrap>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {editable ? "Edit attributes" : "Read only"}
          </Typography>
        </Box>
        {geometryLabel && (
          <Chip size="small" label={geometryLabel} sx={{ bgcolor: "rgba(255,255,255,.2)", color: "#fff", height: 20 }} />
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 1.5, py: 1, overflowY: "auto", flex: 1 }}>
        {columns.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            This layer has no attributes.
          </Typography>
        )}
        {columns.map(({ name, type }) => (
          <TextField
            key={name}
            fullWidth
            size="small"
            margin="dense"
            label={name}
            type={NUMERIC.test(type) ? "number" : "text"}
            inputProps={NUMERIC.test(type) ? { step: "any" } : undefined}
            value={values[name]}
            disabled={!editable}
            onChange={(e) => setValues((p) => ({ ...p, [name]: e.target.value }))}
          />
        ))}
      </Box>

      {editable && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderTop: `1px solid ${C.border}`,
            bgcolor: "#fafbfc",
          }}
        >
          <Button
            variant="contained"
            size="small"
            disableElevation
            disabled={columns.length === 0}
            onClick={() => onApply(values)}
          >
            Apply
          </Button>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Delete this feature (Undo brings it back)">
            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={onDelete}>
              Delete
            </Button>
          </Tooltip>
        </Box>
      )}
    </Paper>
  );
}
